import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Facebook, 
  Instagram, 
  MessageCircle, 
  Mail, 
  Phone, 
  MapPin, 
  Clock,
  ArrowUp,
  Send,
  ChevronDown,
  Leaf
} from 'lucide-react';
import { Link } from 'react-router-dom';

const Footer = () => {
  const [email, setEmail] = useState('');
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [activeSection, setActiveSection] = useState(null);

  const handleSubscribe = (e) => {
    e.preventDefault();
    if (email) {
      setIsSubscribed(true);
      setEmail('');
      setTimeout(() => setIsSubscribed(false), 3000);
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const toggleSection = (section) => {
    setActiveSection(activeSection === section ? null : section);
  };

  const services = [
    'Catálogo Digital',
    'Calculadora PV/SV',
    'Verificador de DNI',
    'Gestión de Eventos',
    'Soporte Técnico',
    'Capacitación en Red'
  ];

  const contactInfo = [
    {
      icon: Phone,
      text: '+51 968039152',
      link: 'https://wa.me/51968039152'
    },
    {
      icon: Mail,
      text: 'soporte@midxn.com',
      link: 'mailto:soporte@midxn.com'
    },
    {
      icon: MapPin,
      text: 'Lima, Perú',
      link: '#'
    },
    {
      icon: Clock,
      text: 'Lun - Sáb: 9:00 AM - 8:00 PM',
      link: '#'
    }
  ];

  const socialLinks = [
    {
      icon: Facebook,
      href: '#',
      color: 'hover:bg-blue-600',
      label: 'Facebook'
    },
    {
      icon: Instagram,
      href: '#',
      color: 'hover:bg-pink-600',
      label: 'Instagram'
    },
    {
      icon: MessageCircle,
      href: 'https://wa.me/51968039152',
      color: 'hover:bg-green-600',
      label: 'WhatsApp'
    }
  ];

  const quickLinks = [
    { to: '/', label: 'Inicio' },
    { to: '/nosotros', label: 'Nosotros' },
/*     { to: '/servicios', label: 'Funciones' },
    { to: '/testimonios', label: 'Testimonios' }, */
    { to: '/contacto', label: 'Contacto' }
  ];

  return (
    <footer className="bg-gradient-to-br from-stone-950 via-stone-900 to-emerald-950 text-stone-300 relative overflow-hidden">
      {/* Patrón de fondo decorativo */}
      <div className="absolute inset-0 opacity-5 pointer-events-none">
        <div className="absolute inset-0 bg-gradient-to-r from-emerald-500 to-teal-500 mix-blend-overlay"></div>
      </div>
      
      <div className="container mx-auto px-4 py-16 relative z-10">
        {/* Sección principal */}
        <div className="grid lg:grid-cols-4 md:grid-cols-2 gap-8 mb-12">
          
          {/* Columna 1: Información de la empresa */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="lg:col-span-1"
          >
            <motion.div
              whileHover={{ scale: 1.02 }}
              className="mb-6"
            >
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-lg flex items-center justify-center">
                  <Leaf className="w-5 h-5 text-white" />
                </div>
                <span className="text-2xl font-bold bg-gradient-to-r from-emerald-400 to-teal-400 bg-clip-text text-transparent">
                  Mi DXN
                </span>
              </div>
              <p className="text-stone-400 leading-relaxed mb-6 text-sm">
                La plataforma tecnológica diseñada para potenciar el crecimiento de distribuidores DXN. 
                Simplificamos tu gestión para que te enfoques en lo que realmente importa: tu red.
              </p>
              
              {/* Badge de certificación */}
              <div className="inline-flex items-center bg-stone-800/50 backdrop-blur-sm rounded-full px-4 py-2 border border-stone-700">
                <div className="w-2 h-2 bg-emerald-400 rounded-full mr-2 animate-pulse"></div>
                <span className="text-xs text-stone-300 font-medium">Soporte 24/7 para Distribuidores</span>
              </div>
            </motion.div>
          </motion.div>

          {/* Columna 2: Enlaces rápidos */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            viewport={{ once: true }}
          >
            <div 
              className="md:hidden flex items-center justify-between cursor-pointer mb-4 p-2 rounded-lg hover:bg-stone-800/50 transition-colors"
              onClick={() => toggleSection('links')}
            >
              <h4 className="text-base font-semibold text-white">Enlaces Rápidos</h4>
              <ChevronDown className={`w-5 h-5 text-stone-400 transition-transform duration-300 ${activeSection === 'links' ? 'rotate-180' : ''}`} />
            </div>
            <h4 className="text-base font-semibold text-white mb-6 hidden md:block">Enlaces Rápidos</h4>
            
            <div className={`${activeSection === 'links' ? 'block' : 'hidden'} md:block`}>
              <ul className="space-y-3">
                {quickLinks.map((link, index) => (
                  <motion.li
                    key={link.to}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    viewport={{ once: true }}
                  >
                    <Link
                      to={link.to}
                      className="text-stone-400 hover:text-emerald-400 transition-all duration-300 flex items-center group text-sm"
                    >
                      <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full mr-3 opacity-0 group-hover:opacity-100 transition-opacity"></span>
                      {link.label}
                    </Link>
                  </motion.li>
                ))}
              </ul>
            </div>
          </motion.div>

          {/* Columna 3: Funcionalidades */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <div 
              className="md:hidden flex items-center justify-between cursor-pointer mb-4 p-2 rounded-lg hover:bg-stone-800/50 transition-colors"
              onClick={() => toggleSection('services')}
            >
              <h4 className="text-base font-semibold text-white">Funcionalidades</h4>
              <ChevronDown className={`w-5 h-5 text-stone-400 transition-transform duration-300 ${activeSection === 'services' ? 'rotate-180' : ''}`} />
            </div>
            <h4 className="text-base font-semibold text-white mb-6 hidden md:block">Funcionalidades</h4>
            
            <div className={`${activeSection === 'services' ? 'block' : 'hidden'} md:block`}>
              <ul className="space-y-3">
                {services.map((service, index) => (
                  <motion.li
                    key={service}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="flex items-center text-stone-400 hover:text-emerald-400 transition-colors text-sm"
                  >
                    <span className="w-1.5 h-1.5 bg-teal-500 rounded-full mr-3"></span>
                    {service}
                  </motion.li>
                ))}
              </ul>
            </div>
          </motion.div>

          {/* Columna 4: Newsletter y Contacto */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            viewport={{ once: true }}
            className="lg:col-span-1"
          >
            <h4 className="text-base font-semibold text-white mb-4">Comunidad Mi DXN</h4>
            <p className="text-stone-400 mb-4 text-sm">
              Recibe tips exclusivos para crecer en tu red y las últimas novedades de la plataforma.
            </p>
            
{/*             <AnimatePresence mode="wait">
              {isSubscribed ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4 text-center mb-4"
                >
                  <div className="text-emerald-400 font-semibold text-sm">¡Bienvenido a la comunidad!</div>
                </motion.div>
              ) : (
                <motion.form
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  onSubmit={handleSubscribe}
                  className="space-y-3 mb-6"
                >
                  <div className="relative">
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="tu@email.com"
                      className="w-full bg-stone-800/50 backdrop-blur-sm border border-stone-700 rounded-xl px-4 py-3 text-sm text-white placeholder-stone-500 focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 transition-all"
                      required
                    />
                    <motion.button
                      type="submit"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-gradient-to-r from-emerald-600 to-teal-600 rounded-lg p-2 hover:from-emerald-500 hover:to-teal-500 transition-all shadow-lg"
                    >
                      <Send className="w-4 h-4 text-white" />
                    </motion.button>
                  </div>
                  <p className="text-xs text-stone-500">
                    Respetamos tu privacidad. Sin spam.
                  </p>
                </motion.form>
              )}
            </AnimatePresence> */}

            {/* Información de contacto compacta */}
            <div className="space-y-3 pt-4 border-t border-stone-800">
              {contactInfo.map((item, index) => (
                <motion.a
                  key={index}
                  href={item.link}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="flex items-center text-stone-400 hover:text-emerald-400 transition-colors group text-sm"
                >
                  <item.icon className="w-4 h-4 mr-3 text-emerald-500 group-hover:text-emerald-400 transition-colors" />
                  <span>{item.text}</span>
                </motion.a>
              ))}
            </div>
          </motion.div>


          
        </div>

        {/* Línea separadora y Copyright */}
        <div className="border-t border-stone-800 pt-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-6 md:space-y-0">
            
            {/* Copyright */}
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="text-stone-500 text-sm text-center md:text-left"
            >
              <p className="mb-2">© 2026 Mi DXN. Todos los derechos reservados.</p>
              <p className="text-xs text-stone-600 mb-3">Distribuidor Independiente. DXN y el logo de DXN son marcas registradas de DXN Corporation.</p>
              <div className="flex flex-wrap justify-center md:justify-start gap-4 text-xs">
                <a href="#" className="hover:text-emerald-400 transition-colors">Política de Privacidad</a>
                <span className="text-stone-700">|</span>
                <a href="#" className="hover:text-emerald-400 transition-colors">Términos de Servicio</a>
                <span className="text-stone-700">|</span>
                <a href="#" className="hover:text-emerald-400 transition-colors">Cookies</a>
              </div>
            </motion.div>

            {/* Redes sociales */}
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
              className="flex flex-col items-center md:items-end space-y-3"
            >
              <span className="text-stone-500 text-sm">Síguenos en redes:</span>
              <div className="flex space-x-3">
                {socialLinks.map((social, index) => (
                  <motion.a
                    key={index}
                    href={social.href}
                    aria-label={social.label}
                    whileHover={{ scale: 1.1, y: -2 }}
                    whileTap={{ scale: 0.9 }}
                    className={`w-10 h-10 bg-stone-800/50 backdrop-blur-sm rounded-full flex items-center justify-center border border-stone-700 text-stone-400 transition-all duration-300 ${social.color} hover:text-white hover:border-transparent`}
                  >
                    <social.icon className="w-5 h-5" />
                  </motion.a>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Botón para volver arriba 
      <motion.button
        onClick={scrollToTop}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        className="fixed bottom-6 right-6 w-12 h-12 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white rounded-full flex items-center justify-center shadow-lg shadow-emerald-900/50 hover:shadow-xl transition-all z-40 border border-emerald-400/20"
        aria-label="Volver arriba"
      >
        <ArrowUp className="w-5 h-5" />
      </motion.button>
*/}

    </footer>
  );
};

export default Footer;